import React from 'react';
import QRCode from 'react-qr-code';
import { Modal } from './Modal';
import { Button } from './Button';
import { useToast } from '../../hooks/useToast';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  url: string;
  hotelName?: string;
}

export const QRCodeModal: React.FC<QRCodeModalProps> = ({
  isOpen,
  onClose,
  url,
  hotelName,
}) => {
  const { success, error } = useToast();

  const handleDownload = () => {
    const svg = document.getElementById('qr-code-svg');
    if (!svg) {
      error('Failed to generate QR code');
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');

      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-code-${hotelName || 'hotel'}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      success('URL copied to clipboard');
    } catch (err) {
      error('Failed to copy URL');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="QR Code - Traveler Facing Page"
      size="md"
    >
      <div className="flex flex-col items-center gap-6">
        {/* QR Code Display */}
        <div className="p-6 bg-white rounded-lg border-2 border-gray-200">
          <QRCode
            id="qr-code-svg"
            value={url}
            size={256}
            level="H"
            fgColor="#000000"
            bgColor="#ffffff"
          />
        </div>

        {/* URL Display */}
        <div className="w-full">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Traveler Facing Page URL:
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={url}
              readOnly
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50"
            />
            <Button variant="outline" onClick={handleCopyUrl}>
              Copy
            </Button>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 text-center">
          Share this QR code with your travelers to direct them to the traveler
          facing page for this hotel.
        </p>

        {/* Action Buttons */}
        <div className="flex gap-3 w-full">
          <Button variant="outline" onClick={handleDownload} className="flex-1">
            Download QR Code
          </Button>
          <Button onClick={onClose} className="flex-1">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};
